import React, { useState } from 'react';
import { SearchIcon, ShoppingBagIcon, PlusIcon, MinusIcon, XIcon, StoreIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface Shop {
  id: number;
  name: string;
  description: string;
  logo: string;
}
interface Product {
  id: number;
  shopId: number;
  name: string;
  price: number;
  description: string;
  image: string;
  category: string;
}
interface CartItem {
  productId: number;
  quantity: number;
  product: Product;
}
interface ShopProductsPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onCreateLink?: () => void;
}
const shops: Shop[] = [{
  id: 1,
  name: 'Urban Outfitters',
  description: 'Contemporary clothing and lifestyle products',
  logo: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}, {
  id: 2,
  name: 'Tech Haven',
  description: 'Latest electronics and gadgets',
  logo: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}, {
  id: 3,
  name: 'Home Essentials',
  description: 'Quality products for your home',
  logo: 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}];
const products: Product[] = [{
  id: 101,
  shopId: 1,
  name: 'Basic T-Shirt',
  price: 599,
  description: 'Comfortable cotton t-shirt',
  image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Clothing'
}, {
  id: 102,
  shopId: 1,
  name: 'Slim Fit Jeans',
  price: 1499,
  description: 'Classic blue denim jeans',
  image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Clothing'
}, {
  id: 103,
  shopId: 1,
  name: 'Leather Wallet',
  price: 899,
  description: 'Genuine leather wallet',
  image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Accessories'
}, {
  id: 201,
  shopId: 2,
  name: 'Premium Headphones',
  price: 2699,
  description: 'Noise cancelling headphones',
  image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 202,
  shopId: 2,
  name: 'Smart Watch',
  price: 5999,
  description: 'Fitness and health tracking',
  image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 203,
  shopId: 2,
  name: 'Wireless Earbuds',
  price: 3899,
  description: 'True wireless Bluetooth earbuds',
  image: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 301,
  shopId: 3,
  name: 'Ceramic Coffee Mug',
  price: 389,
  description: 'Handmade ceramic mug',
  image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Kitchen'
}, {
  id: 302,
  shopId: 3,
  name: 'Decorative Throw Pillow',
  price: 749,
  description: 'Soft decorative pillow',
  image: 'https://images.unsplash.com/photo-1592078615290-033ee584e267?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Home Decor'
}, {
  id: 303,
  shopId: 3,
  name: 'Scented Candle Set',
  price: 1049,
  description: 'Set of 3 scented soy candles',
  image: 'https://images.unsplash.com/photo-1602523069662-0b1ea6503f14?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Home Decor'
}];
const formatTHB = (amount: number) => `฿${amount.toLocaleString('en-US', {
  minimumFractionDigits: 0
})}`;
export function ShopProductsPayment({
  onExpiryDateChange,
  onCreateLink
}: ShopProductsPaymentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<number>(1);
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  const filteredProducts = products.filter(p => p.shopId === selectedShopId && (searchTerm === '' || p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.category.toLowerCase().includes(searchTerm.toLowerCase())));
  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        return prev.map(i => i.productId === product.id ? {
          ...i,
          quantity: i.quantity + 1
        } : i);
      }
      return [...prev, {
        productId: product.id,
        quantity: 1,
        product
      }];
    });
  };
  const removeFromCart = (productId: number) => {
    setCart(prev => prev.filter(i => i.productId !== productId));
  };
  const updateQuantity = (productId: number, qty: number) => {
    if (qty < 1) return;
    setCart(prev => prev.map(i => i.productId === productId ? {
      ...i,
      quantity: qty
    } : i));
  };
  const totalAmount = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    onExpiryDateChange?.(date);
  };
  return <div className="flex gap-5 items-start" data-id="element-4379">
      {/* ─── LEFT COLUMN ─── */}
      <div className="flex-1 space-y-5 min-w-0" data-id="element-4380">
        {/* ─── SHOP & PRODUCTS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-4381">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-4382">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-4383">
              <StoreIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-4384" />
              Shop &amp; products
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-4385">
              Select a shop and add products to the order.
            </p>
          </div>

          {/* Shop selection — edge-to-edge rows */}
          <div data-id="element-4386">
            {shops.map((shop, idx) => <div key={shop.id} className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedShopId === shop.id ? 'bg-[#F7941D]/5' : 'hover:bg-gray-50'} ${idx < shops.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => setSelectedShopId(shop.id)} data-id="element-4387">
                <input type="radio" name="shop" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0" checked={selectedShopId === shop.id} onChange={() => setSelectedShopId(shop.id)} data-id="element-4388" />
                <div className="ml-4 flex items-center flex-1 min-w-0" data-id="element-4389">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-4390">
                    <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" data-id="element-4391" />
                  </div>
                  <div className="ml-3 min-w-0" data-id="element-4392">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4393">
                      {shop.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-4394">
                      {shop.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-t border-gray-200" data-id="element-4395">
            <div className="relative" data-id="element-4396">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4397">
                <SearchIcon size={16} className="text-gray-400" data-id="element-4398" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder="Search products..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-4399" />
            </div>
          </div>

          {/* Product grid */}
          <div className="px-6 pb-6" data-id="element-4400">
            {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4401">
                {filteredProducts.map(product => {
              const inCart = cart.find(i => i.productId === product.id);
              return <div key={product.id} className="border border-gray-200 rounded-lg p-3 flex gap-3" data-id="element-4402">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-4403">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-4404" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4405">
                        <div className="flex items-start justify-between gap-2" data-id="element-4406">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4407">
                            {product.name}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 flex-shrink-0" data-id="element-4408">
                            {formatTHB(product.price)}
                          </p>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-4409">
                          {product.category}
                        </p>
                        <div className="mt-2" data-id="element-4410">
                          <button type="button" onClick={() => addToCart(product)} className="px-2.5 py-1 bg-[#F7941D]/10 text-[#F7941D] hover:bg-[#F7941D]/20 rounded-md text-xs font-medium flex items-center transition-colors" data-id="element-4411">
                            <PlusIcon size={12} className="mr-1" data-id="element-4412" />
                            {inCart ? `Add (${inCart.quantity})` : 'Add'}
                          </button>
                        </div>
                      </div>
                    </div>;
            })}
              </div> : <div className="text-center py-8" data-id="element-4413">
                <SearchIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-4414" />
                <p className="text-sm font-medium text-gray-700" data-id="element-4415">
                  No products found
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4416">
                  Try a different search term
                </p>
              </div>}
          </div>
        </div>

        {/* ─── ADDITIONAL OPTIONS ─── */}
        <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} data-id="element-4417" />
      </div>

      {/* ─── RIGHT COLUMN — ORDER SUMMARY ─── */}
      <div className="w-80 flex-shrink-0 sticky top-6" data-id="element-4418">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-4419">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-4420">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-4421">
              <ShoppingBagIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-4422" />
              Order summary
            </h3>
          </div>

          {cart.length > 0 ? <>
              <div data-id="element-4423">
                {cart.map((item, idx) => <div key={item.productId} className={`px-6 py-3 flex items-center gap-3 ${idx < cart.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-4424">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-4425">
                      <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-4426" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4427">
                      <div className="flex items-start justify-between gap-1" data-id="element-4428">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4429">
                          {item.product.name}
                        </p>
                        <button type="button" onClick={() => removeFromCart(item.productId)} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" data-id="element-4430">
                          <XIcon size={14} data-id="element-4431" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-1" data-id="element-4432">
                        <div className="flex items-center border border-gray-200 rounded-md" data-id="element-4433">
                          <button type="button" onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-1.5 py-0.5 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-4434">
                            <MinusIcon size={12} data-id="element-4435" />
                          </button>
                          <span className="px-2 text-xs font-medium text-gray-900" data-id="element-4436">
                            {item.quantity}
                          </span>
                          <button type="button" onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-1.5 py-0.5 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-4437">
                            <PlusIcon size={12} data-id="element-4438" />
                          </button>
                        </div>
                        <p className="text-xs font-medium text-gray-900" data-id="element-4439">
                          {formatTHB(item.product.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/50" data-id="element-4440">
                <div className="flex items-center justify-between" data-id="element-4441">
                  <span className="text-sm text-gray-500" data-id="element-4442">Total</span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-4443">
                    {formatTHB(totalAmount)}
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1" data-id="element-4444">
                  {totalItems} item{totalItems !== 1 ? 's' : ''}
                </p>
              </div>
            </> : <div className="px-6 py-10 text-center" data-id="element-4445">
              <ShoppingBagIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-4446" />
              <p className="text-sm font-medium text-gray-700" data-id="element-4447">
                No products selected
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-4448">
                Add products from the shop
              </p>
            </div>}
        </div>

        {/* Create link button */}
        <button type="button" disabled={cart.length === 0} onClick={onCreateLink} className={`w-full mt-4 px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center justify-center ${cart.length > 0 ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-4449">
          Create payment link
        </button>
      </div>
    </div>;
}